import React, { useState, useEffect, useCallback } from 'react';
import { Crown, Star, Diamond, Plus } from 'lucide-react';
import api from '../../api';
import { useAuth } from '../../context/AuthContext';
import SubscriptionPlans from './SubscriptionPlans';

const SubscriptionStatus = () => {
  const { user } = useAuth();
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [showPlans, setShowPlans] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchCurrentSubscription = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/subscriptions/creators/${user.userId}/current`);
      setCurrentSubscription(response.data);
    } catch (error) {
      // No active subscription
      setCurrentSubscription(null);
    } finally {
      setLoading(false);
    }
  }, [user.userId]);

  useEffect(() => {
    fetchCurrentSubscription();
  }, [fetchCurrentSubscription]);

  const getTierIcon = (tier) => {
    switch (tier) {
      case 'SILVER':
        return <Star className="h-4 w-4 text-gray-400" />;
      case 'GOLD':
        return <Crown className="h-4 w-4 text-yellow-500" />;
      case 'PLATINUM':
        return <Diamond className="h-4 w-4 text-purple-500" />;
      default:
        return null;
    }
  };

  const getTierColor = (tier) => {
    switch (tier) {
      case 'SILVER':
        return 'bg-gray-100 text-gray-800 border-gray-300';
      case 'GOLD':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'PLATINUM':
        return 'bg-purple-100 text-purple-800 border-purple-300';
      default:
        return 'bg-blue-100 text-blue-800 border-blue-300';
    }
  };

  const handleSubscriptionSuccess = () => {
    fetchCurrentSubscription();
    setShowPlans(false);
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-4">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-2"></div>
          <div className="h-3 bg-gray-200 rounded w-1/2"></div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Subscription Status</h3>
            {currentSubscription ? (
              <div className="mt-2">
                <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getTierColor(currentSubscription.tier)}`}>
                  {getTierIcon(currentSubscription.tier)}
                  <span className="ml-1">{currentSubscription.tier}</span>
                </div>
                <div className="mt-2 text-sm text-gray-600">
                  <p>Credits: {currentSubscription.creditsRemaining || 0}</p>
                  <p>Expires: {new Date(currentSubscription.endDate).toLocaleDateString()}</p>
                </div>
              </div>
            ) : (
              <div className="mt-2">
                <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800 border border-gray-300">
                  Bronze (Free)
                </div>
                <p className="mt-2 text-sm text-gray-600">
                  View-only access. Upgrade to start bidding on campaigns!
                </p>
              </div>
            )}
          </div>
          <button
            onClick={() => setShowPlans(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <Plus className="h-4 w-4 mr-1" />
            {currentSubscription ? 'Upgrade' : 'Subscribe'}
          </button>
        </div>
      </div>

      <SubscriptionPlans
        isOpen={showPlans}
        onClose={() => setShowPlans(false)}
        onSubscriptionSuccess={handleSubscriptionSuccess}
      />
    </>
  );
};

export default SubscriptionStatus;

